import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import LastUpdated from "../components/LastUpdated";
import { Link } from "react-router-dom";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import { SERVICE_AREAS, COMPANY, PAGE_UPDATED, webPageLd, breadcrumbLd } from "../data/site";

/* eslint-disable react/no-unescaped-entities */

const groups = [
  {
    name: "Central Austin",
    areas: ["Austin", "Tarrytown", "Mueller", "East Austin", "South Austin", "North Austin", "Sunset Valley"],
  },
  { name: "West Austin & Hill Country", areas: ["Westlake", "West Lake Hills", "Lakeway", "Bee Cave", "Spicewood", "Lago Vista", "Dripping Springs", "Wimberley"] },
  { name: "North Suburbs", areas: ["Round Rock", "Cedar Park", "Leander", "Pflugerville", "Georgetown", "Hutto", "Jollyville", "Avery Ranch", "Brushy Creek", "Liberty Hill", "Bartlett"] },
  { name: "South Suburbs", areas: ["Buda", "Kyle", "Manchaca", "Circle C Ranch", "Del Valle", "Manor"] },
  { name: "Premium Neighborhoods", areas: ["Steiner Ranch"] },
];

export default function ServiceAreasPage() {
  return (
    <>
      <Seo
        title="Service Areas: Austin TX & Suburbs | Texan Strong Builds"
        description="Texan Strong Builds serves Austin TX and every suburb — Round Rock, Cedar Park, Leander, Pflugerville, Georgetown, Lakeway and the Hill Country."
        canonical={`${window.location.origin}/service-areas`}
        jsonLd={[
          webPageLd({
            name: "Service Areas | Concrete & Landscape Contractor Austin TX & Suburbs",
            path: "/service-areas",
            updated: PAGE_UPDATED.serviceAreas,
            description: "Texan Strong Builds serves Austin and every surrounding suburb across the Hill Country.",
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Service Areas", path: "/service-areas" },
          ]),
        ]}
      />

      <section className="bg-stone-900 text-white pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Breadcrumbs dark items={[{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }]} />
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-500">Service areas</div>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
            Austin and<br />everywhere around it.
          </h1>
          <p className="mt-7 text-stone-300 text-lg max-w-3xl">
            Texan Strong Builds is a local Austin, TX team — most projects are within 40 miles of
            the Texas capitol. If you don't see your neighborhood listed, call (512) 781-8851;
            Texan Strong Builds probably covers it.
          </p>
          <LastUpdated dark date={PAGE_UPDATED.serviceAreas} className="mt-5" />
        </div>
      </section>

      <section className="bg-stone-50 py-20 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {groups.map((g) => (
            <div key={g.name} className="bg-white border border-stone-200 p-7" data-testid={`area-group-${g.name.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-red-600">Region</div>
              <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-stone-900">{g.name}</h2>
              <ul className="mt-5 space-y-2.5">
                {g.areas.map((a) => (
                  <li key={a} className="flex items-center gap-2 text-stone-700">
                    <MapPin size={14} className="text-stone-400" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="font-display text-3xl font-black tracking-tighter text-stone-900">All neighborhoods</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {SERVICE_AREAS.map((a) => (
              <span key={a} className="px-3 py-1.5 border border-stone-300 text-sm text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors cursor-default">
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <h3 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[1.02]">
            Ready when you are.
          </h3>
          <p className="mt-4 text-stone-300 text-lg">
            One call. Free estimate. We come to you anywhere in the Austin metro.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contact" className="btn-primary">
              Get a Free Estimate <ArrowRight size={18} />
            </Link>
            <a href={COMPANY.phoneTel} className="btn-ghost-dark">
              <Phone size={18} /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
